// Calendar dates and the repeating schedule. Dates are local calendar days as
// "YYYY-MM-DD" strings, with no time or time zone, so a plan for "Tuesday"
// means the same thing wherever the phone is. "Today" depends on the person's
// time zone (todayIn).
import { z } from 'zod';
import { Units } from './auth.ts';
import { PlannedExercise } from './planner.ts';
import { Split } from './training.ts';

const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;
export const IsoDate = z
  .string()
  .regex(DATE_SHAPE, 'Expected a date like 2026-10-09')
  // Zod runs every check even after one fails, so this one must cope with any string.
  .refine((d) => !DATE_SHAPE.test(d) || fromDayNumber(dayNumber(d)) === d, 'Not a real date');

const DAY_MS = 86_400_000;

/** Days since 1970-01-01. */
export function dayNumber(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

export function fromDayNumber(n: number): string {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

export const addDays = (date: string, days: number) => fromDayNumber(dayNumber(date) + days);
export const daysBetween = (from: string, to: string) => dayNumber(to) - dayNumber(from);

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** 0 = Sunday … 6 = Saturday, like Date.getDay(). (1970-01-01 was a Thursday.) */
export const weekday = (date: string) => mod(dayNumber(date) + 4, 7);
/** Weeks start on Sunday. */
export const startOfWeek = (date: string) => addDays(date, -weekday(date));

/** Today's date where the person is. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/**
 * One version of someone's repeating schedule: a cycle of `slots` (a split id,
 * or null for rest) that starts at `anchorDate`, in effect from `effectiveFrom`
 * until a later version takes over. A 7-slot cycle is a weekly plan; any other
 * length is a rotation.
 */
export type PatternVersion = { id: string; effectiveFrom: string; anchorDate: string; slots: (string | null)[] };

/** The version in effect on a date: the latest one starting on or before it. */
export function activePattern(versions: readonly PatternVersion[], date: string): PatternVersion | null {
  let best: PatternVersion | null = null;
  for (const v of versions) {
    if (v.effectiveFrom > date) continue;
    // Later start wins; for the same start, the later-created (uuidv7 sorts by time).
    if (!best || v.effectiveFrom > best.effectiveFrom || (v.effectiveFrom === best.effectiveFrom && v.id > best.id)) best = v;
  }
  return best;
}

/** Which slot of the cycle a date falls on. */
export const slotIndex = (p: PatternVersion, date: string) => mod(daysBetween(p.anchorDate, date), p.slots.length);

/**
 * Moves a weekly (7-day) pattern's anchor without changing which weekday gets
 * which split: the slots rotate to match. (For rotations, moving the anchor is
 * meant to shift the cycle, so they're returned unchanged.)
 */
export function reanchorWeekly(anchorDate: string, slots: (string | null)[], newAnchor: string): (string | null)[] {
  if (slots.length !== 7) return slots;
  const shift = mod(daysBetween(anchorDate, newAnchor), 7);
  return slots.map((_, i) => slots[(i + shift) % 7]!);
}

export const DayKind = z.enum(['split', 'rest', 'skip', 'unplanned']);
export type DayKind = z.infer<typeof DayKind>;

/** A one-day change to the pattern's plan. */
export type DayOverride = { kind: 'split' | 'rest' | 'skip'; splitId: string | null };

export type ResolvedDay = {
  date: string;
  kind: DayKind;
  splitId: string | null;
  /** override: edited for this day. pattern: from the repeating schedule. none: no schedule yet. */
  source: 'override' | 'pattern' | 'none';
};

export function resolveDay(versions: readonly PatternVersion[], override: DayOverride | undefined, date: string): ResolvedDay {
  if (override) return { date, kind: override.kind, splitId: override.splitId, source: 'override' };
  const p = activePattern(versions, date);
  if (!p) return { date, kind: 'unplanned', splitId: null, source: 'none' };
  const splitId = p.slots[slotIndex(p, date)] ?? null;
  return { date, kind: splitId ? 'split' : 'rest', splitId, source: 'pattern' };
}

// --- API shapes ---

export const PatternInput = z
  .object({
    /** First day of the cycle (slot 0). */
    anchorDate: IsoDate,
    /** The new pattern replaces the old one from this date; earlier days keep the old plan. */
    effectiveFrom: IsoDate,
    slots: z.array(z.string().nullable()).min(1, 'Add at least one day').max(14, 'Cycles can be up to 14 days'),
  })
  .refine((p) => p.slots.some((s) => s !== null), 'Add at least one training day');
export type PatternInput = z.infer<typeof PatternInput>;

export const PatternResponse = z.object({
  pattern: z
    .object({ id: z.string(), anchorDate: z.string(), effectiveFrom: z.string(), slots: z.array(z.string().nullable()) })
    .nullable(),
});

export const CalendarDay = z.object({
  date: z.string(),
  userId: z.string(),
  kind: DayKind,
  splitId: z.string().nullable(),
  source: z.enum(['override', 'pattern', 'none']),
  /** Others training with this person that day. */
  together: z.array(z.string()),
  /** The planned exercises (split days only): the split's template, or the day's own list when `exercisesEdited`. */
  exercises: z.array(PlannedExercise).default([]),
  exercisesEdited: z.boolean().default(false),
  /** What to train (split, rest, skip) was changed for this day, not just its exercise list. */
  planChanged: z.boolean().default(false),
});
export type CalendarDay = z.infer<typeof CalendarDay>;

export const CalendarResponse = z.object({
  from: z.string(),
  to: z.string(),
  users: z.array(z.object({ id: z.string(), displayName: z.string(), units: Units.default('lb') })),
  /** Every split referenced in `days` (archived ones included). */
  splits: z.array(Split.extend({ userId: z.string(), archived: z.boolean() })),
  days: z.array(CalendarDay),
});
export type CalendarResponse = z.infer<typeof CalendarResponse>;

export const SetDayRequest = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('split'), splitId: z.string() }),
  z.object({ kind: z.literal('rest') }),
  z.object({ kind: z.literal('skip') }),
]);
export type SetDayRequest = z.infer<typeof SetDayRequest>;

export const SwapDaysRequest = z.object({ a: IsoDate, b: IsoDate });
export const PushForwardRequest = z.object({ missedDate: IsoDate });
export const TogetherRequest = z.object({ withUserId: z.string().min(1) });
